import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RotateCcw, Send, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import EmptyState from '@/components/shared/EmptyState';
import { api, ApiError } from '@/api/apiClient';
import { enqueue } from '@/lib/outbox';
import { notifyEnqueued } from '@/lib/syncEngine';

const fmt2 = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Create a sales return from a confirmed order.
 *
 * Flow: pick quantities to return per line → submit → the return goes to the
 * supervisor for approval. On approval the backend prepares a DRAFT credit note
 * + DRAFT return picking for the accountant to finalise.
 */
export default function NewReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = parseInt(params.get('order_id'), 10) || null;

  const [data, setData] = useState(null);   // { order, lines }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState({});       // { sale_line_id: number }
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) { setError('لم يتم تحديد أمر البيع'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const res = await api.get('/sellgo_v5_order_returnable', { order_id: orderId });
      if (res?.success && res.data) setData(res.data);
      else setError(res?.error || 'تعذّر تحميل بيانات الأمر');
    } catch (e) {
      setError(e?.message || 'تعذّر تحميل بيانات الأمر');
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const setLineQty = (lineId, value, max) => {
    let v = Number(value);
    if (Number.isNaN(v) || v < 0) v = 0;
    if (v > max) v = max;
    setQty((q) => ({ ...q, [lineId]: v }));
  };

  const total = useMemo(() => {
    if (!data) return 0;
    return data.lines.reduce((sum, l) => sum + (qty[l.sale_line_id] || 0) * (l.price_unit || 0), 0);
  }, [data, qty]);

  const selectedCount = useMemo(
    () => Object.values(qty).filter((v) => v > 0).length,
    [qty],
  );

  const handleSubmit = async () => {
    const lines = (data?.lines || [])
      .filter((l) => (qty[l.sale_line_id] || 0) > 0)
      .map((l) => ({ sale_line_id: l.sale_line_id, quantity: qty[l.sale_line_id] }));
    if (lines.length === 0) { toast.error('اختر صنفاً واحداً على الأقل بكمية أكبر من صفر'); return; }
    setSubmitting(true);
    const body = { sale_order_id: orderId, reason: reason.trim(), lines };

    // Offline: queue the return and sync later.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await enqueue('return', { body });
      notifyEnqueued();
      toast.success('تم حفظ المرتجع — هيترفع تلقائياً لما النت يرجع');
      setSubmitting(false);
      navigate('/returns');
      return;
    }

    try {
      const res = await api.post('/sellgo_v5_return_create', body);
      if (res?.success && res.data) {
        toast.success('تم إرسال المرتجع للاعتماد');
        navigate(`/returns/${res.data.id}`, { replace: true });
      } else {
        toast.error(res?.error || 'تعذّر إنشاء المرتجع');
      }
    } catch (e) {
      if (e instanceof ApiError && (!e.status || e.status === 0)) {
        await enqueue('return', { body });
        notifyEnqueued();
        toast.success('تم حفظ المرتجع — هيترفع تلقائياً لما النت يرجع');
        navigate('/returns');
      } else {
        toast.error(e?.message || 'تعذّر إنشاء المرتجع');
      }
    }
    setSubmitting(false);
  };

  if (loading) return <LoadingState message="جاري تحميل أصناف الأمر..." />;
  if (error) return <ErrorState message={error} onRetry={orderId ? load : undefined} />;

  const order = data.order;
  const hasReturnable = data.lines.some((l) => l.returnable_qty > 0);

  return (
    <div className="pb-28 max-w-2xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="px-4 pt-4">
        <div className="flex items-center gap-2 mb-1 text-primary">
          <RotateCcw className="w-5 h-5" />
          <h1 className="text-lg font-bold">إنشاء مرتجع</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          من أمر البيع <span className="font-semibold text-foreground">{order.name}</span>
          {Array.isArray(order.partner_id) && <> — {order.partner_id[1]}</>}
        </p>
      </div>

      {/* Lines */}
      <div className="px-4 mt-4 space-y-2">
        {!hasReturnable && (
          <EmptyState
            icon={Package}
            title="لا توجد أصناف قابلة للإرجاع"
            description="كل الأصناف في هذا الأمر تم إرجاعها بالكامل بالفعل."
          />
        )}
        {data.lines.map((l) => {
          const max = l.returnable_qty;
          const disabled = max <= 0;
          const current = qty[l.sale_line_id] || 0;
          return (
            <motion.div
              key={l.sale_line_id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className={`bg-card border rounded-2xl p-3 ${current > 0 ? 'border-primary/50' : 'border-border'} ${disabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-foreground truncate">
                    {Array.isArray(l.product_id) ? l.product_id[1] : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    سعر الوحدة: {fmt2(l.price_unit)} • المتاح للإرجاع: <span className="font-semibold text-foreground">{fmt2(max)}</span>
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <input
                    type="number" inputMode="decimal" min={0} max={max} step="any"
                    disabled={disabled}
                    value={current || ''}
                    onChange={(e) => setLineQty(l.sale_line_id, e.target.value, max)}
                    placeholder="0"
                    className="w-24 text-center border border-border rounded-xl h-10 px-2 text-sm font-semibold focus:border-primary focus:outline-none disabled:bg-muted"
                  />
                  {current > 0 && (
                    <span className="text-[11px] text-primary font-semibold">
                      = {fmt2(current * (l.price_unit || 0))}
                    </span>
                  )}
                </div>
              </div>
              {!disabled && (
                <button
                  onClick={() => setLineQty(l.sale_line_id, max, max)}
                  className="mt-2 text-[11px] text-primary hover:underline"
                >
                  إرجاع الكمية كاملة ({fmt2(max)})
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Reason */}
      {hasReturnable && (
        <div className="px-4 mt-4">
          <label className="text-sm font-semibold text-foreground">سبب المرتجع (اختياري)</label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="مثال: تالف / خطأ في الصنف / رفض العميل..."
            className="mt-1 w-full border border-border rounded-xl p-3 text-sm focus:border-primary focus:outline-none resize-none"
          />
        </div>
      )}

      {/* Sticky submit */}
      {hasReturnable && (
        <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-shrink-0">
              <p className="text-[11px] text-muted-foreground leading-none">الإجمالي</p>
              <p className="text-base font-bold text-foreground">{fmt2(total)}</p>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedCount === 0}
              className="flex-1 bg-primary rounded-xl h-12 gap-2 font-semibold"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'جاري الإرسال...' : `إرسال للاعتماد${selectedCount ? ` (${selectedCount})` : ''}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
